import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader, DotLegalMultiSelect, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import { useCreateEditTemplateDialog } from "./CreateEditTemplateDialog.hooks.ts";
import { TemplatePlanRequirement } from "../templates/Templates.types.ts";
import { FrameworkTemplateSaveModel } from "./CreateEditTemplateDialog.types.ts";
import { getTemplateRequirementPlanName } from "../templates/Templates.hooks.ts";
import DotLegalTabs from "../../common/components/dotLegalTabs/DotLegalTabs.tsx";
import React from "react";
import { TabContent } from "../../common/components/dotLegalTabs/DotLegalTabs.types.ts";

export interface CreateEditTemplateDialogProps {
    onCloseDialog: () => void;
    editModel?: { templateId: string; model: FrameworkTemplateSaveModel };
    onTemplateEdited?: () => void;
}

function CreateEditTemplateDialog(props: CreateEditTemplateDialogProps) {
    const {
        isEdit,
        saveModel,
        modelUpdater,
        showErrors,
        complianceAreaIsLoading,
        complianceAreas,
        isSaving,
        onSaveTemplate,
        selectedTabIndex,
        setSelectedTabIndex,
    } = useCreateEditTemplateDialog(props);

    let tabContents: TabContent[] = ["en", "da"].map((lang, i) => {
        let langModel = saveModel.languages.find((l) => l.twoLetterIsoLanguage === lang)!;
        return {
            label: lang,
            index: i,
            content: (
                <>
                    <DotLegalTextField
                        noMargin
                        debounce={false}
                        label="Name of framework"
                        value={langModel.name ?? ""}
                        onChange={(value) => modelUpdater.onNameChange(value, langModel.twoLetterIsoLanguage)}
                        errorText={showErrors && langModel.isNameInvalid() ? "Field is required" : undefined}
                    />

                    <DotLegalTextField
                        multiline
                        rows={5}
                        debounce
                        label="Description"
                        value={langModel.description ?? ""}
                        onChange={(value) => modelUpdater.onDescriptionChange(value, langModel.twoLetterIsoLanguage)}
                    />
                </>
            ),
        };
    });

    return (
        <DotLegalDialog
            buttonOkText={isEdit ? "Save" : "Create"}
            open
            onDialogClose={props.onCloseDialog}
            onOkClick={() => onSaveTemplate()}
            size={"sm"}
            header={`${isEdit ? "Edit" : "Create"} framework template`}
            inProgress={isSaving}
        >
            <DotLegalTabs tabStyle={"secondary"} selectedTab={selectedTabIndex} setSelectedTab={setSelectedTabIndex} content={tabContents} fullWidth border />

            <DotLegalMultiSelect
                options={complianceAreas}
                chosenOptions={saveModel.complianceAreas}
                onChange={modelUpdater.onComplianceAreaChange}
                label={"Compliance areas"}
                noOptionsLabel={"No options"}
                isLoading={complianceAreaIsLoading}
            />

            <DotLegalHeader marginTop={2} fontWeight={400} marginBottom={0} headerStyle="small">
                Which plans should have access to the template?
            </DotLegalHeader>
            <Box ml={"-9px"}>
                <DotLegalCheckbox
                    checked={saveModel.templatePlanRequirements.length === 0}
                    label={"Available on .legal Compliance Platform (Free)"}
                    disabled
                ></DotLegalCheckbox>
            </Box>
            <DotLegalMultiSelect
                options={[
                    { name: getTemplateRequirementPlanName(TemplatePlanRequirement.DataProtection), id: TemplatePlanRequirement.DataProtection.toString() },
                    {
                        name: getTemplateRequirementPlanName(TemplatePlanRequirement.InformationSecurity),
                        id: TemplatePlanRequirement.InformationSecurity.toString(),
                    },
                    { name: getTemplateRequirementPlanName(TemplatePlanRequirement.VendorManagement), id: TemplatePlanRequirement.VendorManagement.toString() },
                    {
                        name: getTemplateRequirementPlanName(TemplatePlanRequirement.ContractManagement),
                        id: TemplatePlanRequirement.ContractManagement.toString(),
                    },
                    { name: getTemplateRequirementPlanName(TemplatePlanRequirement.Frameworks), id: TemplatePlanRequirement.Frameworks.toString() },
                ]}
                chosenOptions={saveModel.templatePlanRequirements.map((x) => x.toString())}
                onChange={modelUpdater.onTemplatePlanRequirementsChange}
                placeholder={"Plans"}
                label={"Plans"}
                noOptionsLabel={"No options"}
            />
        </DotLegalDialog>
    );
}

export default CreateEditTemplateDialog;
